/**
 * Pure session store for preferences questionnaire (answers + cursor).
 *
 * Story of answering:
 * 1. Multi-select / highlight → UpdateDraftAnswer (draft only)
 * 2. Confirm → AnswerQuestion + outcome (next question / preference type done / session done)
 * 3. Leaving is NOT stored here — the hook builds a Leave plan and navigates
 *
 * Belongs here: draft/committed maps, preference-type/question indices.
 * Does not belong here: navigation, HTTP, leave plans, or advance rules.
 */
import type { PreferencesAnswersMap } from '../setup/types'
import { PreferencesClearAnswersMode } from '../setup/types'
import type { PreferencesQuestionnaireMap } from '../../question-utils/preferencesMappers'
import { areAnswersEqual } from '../../question-utils/normalizeAnswersMap'
import {
  getPreferencesAnswersAfterAdd,
  getPreferencesAnswersAfterEdit
} from './flowRules'

/** Bump only when committed content actually changes. */
function nextCommittedAnswersVersion({
  previousAnswers,
  nextAnswers,
  previousVersion
}: {
  previousAnswers: PreferencesAnswersMap
  nextAnswers: PreferencesAnswersMap
  previousVersion: number
}): number {
  if (areAnswersEqual(previousAnswers, nextAnswers)) return previousVersion
  return previousVersion + 1
}

/**
 * What AnswerQuestion does after writing the answer into draft.
 * Chosen by the session hook from flowRules — not by the UI.
 */
export enum PreferencesConfirmOutcome {
  /** Still in this preference type — move to another question. */
  NextQuestion = 'next_question',
  /** Finished this preference type — jump to the next type at question 0. */
  PreferenceTypeDone = 'preference_type_done',
  /** All done answering — draft and committed become the same map. */
  AllDone = 'all_done'
}

export type PreferencesConfirmOutcomePayload =
  | {
      kind: PreferencesConfirmOutcome.NextQuestion
      preferenceTypeIndex: number
      questionIndex: number
    }
  | {
      kind: PreferencesConfirmOutcome.PreferenceTypeDone
      nextPreferenceTypeIndex: number
    }
  | { kind: PreferencesConfirmOutcome.AllDone }

export enum PreferencesSessionActionType {
  /**
   * Full-flow hydrate gateway: set draft + committed (and cursor) from policy.
   * Replaces SeedFromPayloadIfEmpty — may wipe to {} when status is not done.
   */
  ApplyHydration = 'apply_hydration',
  /** Selection / multi-select only — updates draftAnswers; does not move or commit. */
  UpdateDraftAnswer = 'update_draft_answer',
  /** User confirmed this answer — updates draft (and committed if AllDone). */
  AnswerQuestion = 'answer_question',
  MoveCursor = 'move_cursor',
  /** Park cursor at type 0 / question 0 (leave / clear — mirrors strategy ResetCursor). */
  ResetCursor = 'reset_cursor',
  ClearQuestionAnswer = 'clear_question_answer',
  DiscardDraft = 'discard_draft',
  /**
   * Promote current draft → committed without a new AnswerQuestion.
   * Used when the user taps Next on the last multi-select step (answer already in draft).
   */
  CommitDraftAnswers = 'commit_draft_answers',
  PreparePreferenceTypeEdit = 'prepare_preference_type_edit',
  BeginEditAll = 'begin_edit_all',
  /** Hub resume — user opened full preferences review at least once this session. */
  MarkReachedReview = 'mark_reached_review',
  /** After successful POST — align to the answersVersion that was actually submitted. */
  MarkSubmittedAnswers = 'mark_submitted_answers'
}

export type PreferencesSessionState = {
  /** Answers the UI shows while the user is still answering. */
  draftAnswers: PreferencesAnswersMap
  /** Last finished / seeded answers (review + submit source of truth). */
  committedAnswers: PreferencesAnswersMap
  preferenceTypeIndex: number
  questionIndex: number
  /** Hub resume: after full review once, open review instead of list. */
  hasReachedReviewOnce: boolean
  /** Increments when committed answer content changes. */
  committedAnswersVersion: number
  /** answersVersion from the last successful POST (in-flight-safe). */
  lastSubmittedAnswersVersion: number
}

export const initialPreferencesSessionState: PreferencesSessionState = {
  draftAnswers: {},
  committedAnswers: {},
  preferenceTypeIndex: 0,
  questionIndex: 0,
  hasReachedReviewOnce: false,
  committedAnswersVersion: 0,
  lastSubmittedAnswersVersion: 0
}

export type PreferencesSessionAction =
  | {
      type: PreferencesSessionActionType.ApplyHydration
      draftAnswers: PreferencesAnswersMap
      committedAnswers: PreferencesAnswersMap
      /** When true, park cursor at type 0 / question 0. */
      resetCursorToStart: boolean
      /** Wipe → false; seed → true; omit to leave unchanged. */
      hasReachedReviewOnce?: boolean
      /**
       * Version policy from hydration plan — do not infer from empty maps.
       * wipe → 0/0; seed → bump if content changed, then align submitted.
       */
      answersVersionPolicy: 'wipe' | 'seed'
    }
  | {
      type: PreferencesSessionActionType.UpdateDraftAnswer
      questionId: string
      value: string | number | string[]
      questionnaireMap: PreferencesQuestionnaireMap
    }
  | {
      type: PreferencesSessionActionType.AnswerQuestion
      questionId: string
      value: string | number | string[]
      questionnaireMap: PreferencesQuestionnaireMap
      outcome: PreferencesConfirmOutcomePayload
    }
  | {
      type: PreferencesSessionActionType.MoveCursor
      preferenceTypeIndex: number
      questionIndex: number
    }
  | { type: PreferencesSessionActionType.ResetCursor }
  | { type: PreferencesSessionActionType.ClearQuestionAnswer; questionId: string }
  | { type: PreferencesSessionActionType.DiscardDraft }
  | { type: PreferencesSessionActionType.CommitDraftAnswers }
  | {
      type: PreferencesSessionActionType.PreparePreferenceTypeEdit
      preferenceTypeId: string
      preferenceTypeIndex: number
      questionnaireMap: PreferencesQuestionnaireMap
    }
  | {
      type: PreferencesSessionActionType.BeginEditAll
      questionnaireMap: PreferencesQuestionnaireMap
    }
  | { type: PreferencesSessionActionType.MarkReachedReview }
  | {
      type: PreferencesSessionActionType.MarkSubmittedAnswers
      lastSubmittedAnswersVersion: number
    }

/** Pure reducer — no navigation, no HTTP. */
export function preferencesSessionReducer(
  state: PreferencesSessionState,
  action: PreferencesSessionAction
): PreferencesSessionState {
  switch (action.type) {
    case PreferencesSessionActionType.ApplyHydration: {
      // Full-flow gateway only — single-type edit uses PreparePreferenceTypeEdit
      if (action.answersVersionPolicy === 'wipe') {
        return {
          ...state,
          draftAnswers: action.draftAnswers,
          committedAnswers: action.committedAnswers,
          committedAnswersVersion: 0,
          lastSubmittedAnswersVersion: 0,
          ...(action.resetCursorToStart
            ? { preferenceTypeIndex: 0, questionIndex: 0 }
            : {}),
          ...(action.hasReachedReviewOnce !== undefined
            ? { hasReachedReviewOnce: action.hasReachedReviewOnce }
            : {})
        }
      }

      // seed — server answers are already submitted
      const committedAnswersVersion = nextCommittedAnswersVersion({
        previousAnswers: state.committedAnswers,
        nextAnswers: action.committedAnswers,
        previousVersion: state.committedAnswersVersion
      })
      return {
        ...state,
        draftAnswers: action.draftAnswers,
        committedAnswers: action.committedAnswers,
        committedAnswersVersion,
        lastSubmittedAnswersVersion: committedAnswersVersion,
        ...(action.resetCursorToStart
          ? { preferenceTypeIndex: 0, questionIndex: 0 }
          : {}),
        ...(action.hasReachedReviewOnce !== undefined
          ? { hasReachedReviewOnce: action.hasReachedReviewOnce }
          : {})
      }
    }

    case PreferencesSessionActionType.UpdateDraftAnswer: {
      // Draft only — multi-select / highlight; no cursor move or commit
      const nextDraftAnswers = getPreferencesAnswersAfterAdd(
        state.draftAnswers,
        action.questionId,
        action.value,
        action.questionnaireMap
      )
      return { ...state, draftAnswers: nextDraftAnswers }
    }

    case PreferencesSessionActionType.AnswerQuestion: {
      const nextDraftAnswers = getPreferencesAnswersAfterAdd(
        state.draftAnswers,
        action.questionId,
        action.value,
        action.questionnaireMap
      )
      const { outcome } = action

      if (outcome.kind === PreferencesConfirmOutcome.NextQuestion) {
        return {
          ...state,
          draftAnswers: nextDraftAnswers,
          preferenceTypeIndex: outcome.preferenceTypeIndex,
          questionIndex: outcome.questionIndex
        }
      }

      // PreferenceTypeDone: advance to next type at question 0 (page-flow may slide forward)
      if (outcome.kind === PreferencesConfirmOutcome.PreferenceTypeDone) {
        return {
          ...state,
          draftAnswers: nextDraftAnswers,
          preferenceTypeIndex: outcome.nextPreferenceTypeIndex,
          questionIndex: 0
        }
      }

      /**
       * AllDone: one map becomes both draft and committed.
       * Keep cursor — resetting to 0 flashed the first type before leave navigated away.
       */
      const committedAnswers = { ...nextDraftAnswers }
      return {
        ...state,
        draftAnswers: committedAnswers,
        committedAnswers,
        committedAnswersVersion: nextCommittedAnswersVersion({
          previousAnswers: state.committedAnswers,
          nextAnswers: committedAnswers,
          previousVersion: state.committedAnswersVersion
        })
      }
    }

    case PreferencesSessionActionType.MoveCursor:
      return {
        ...state,
        preferenceTypeIndex: action.preferenceTypeIndex,
        questionIndex: action.questionIndex
      }

    case PreferencesSessionActionType.ResetCursor:
      return { ...state, preferenceTypeIndex: 0, questionIndex: 0 }

    case PreferencesSessionActionType.ClearQuestionAnswer: {
      const nextDraftAnswers = { ...state.draftAnswers }
      delete nextDraftAnswers[action.questionId]
      return { ...state, draftAnswers: nextDraftAnswers }
    }

    case PreferencesSessionActionType.DiscardDraft:
      return {
        ...state,
        draftAnswers: { ...state.committedAnswers }
      }

    case PreferencesSessionActionType.CommitDraftAnswers: {
      // Same commit as AllDone, but without writing a new answer value
      const committedAnswers = { ...state.draftAnswers }
      return {
        ...state,
        draftAnswers: committedAnswers,
        committedAnswers,
        committedAnswersVersion: nextCommittedAnswersVersion({
          previousAnswers: state.committedAnswers,
          nextAnswers: committedAnswers,
          previousVersion: state.committedAnswersVersion
        })
      }
    }

    case PreferencesSessionActionType.PreparePreferenceTypeEdit:
      return {
        ...state,
        draftAnswers: getPreferencesAnswersAfterEdit(
          state.committedAnswers,
          PreferencesClearAnswersMode.PREFERENCE_TYPE,
          { preferenceTypeId: action.preferenceTypeId },
          action.questionnaireMap
        ),
        preferenceTypeIndex: action.preferenceTypeIndex,
        questionIndex: 0
      }

    case PreferencesSessionActionType.BeginEditAll:
      return {
        ...state,
        draftAnswers: getPreferencesAnswersAfterEdit(
          state.committedAnswers,
          PreferencesClearAnswersMode.ALL,
          undefined,
          action.questionnaireMap
        ),
        preferenceTypeIndex: 0,
        questionIndex: 0
      }

    case PreferencesSessionActionType.MarkReachedReview:
      if (state.hasReachedReviewOnce) return state
      return { ...state, hasReachedReviewOnce: true }

    case PreferencesSessionActionType.MarkSubmittedAnswers:
      return {
        ...state,
        lastSubmittedAnswersVersion: action.lastSubmittedAnswersVersion
      }

    default:
      return state
  }
}
